import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { User, Menu, X, ArrowRight, LogOut, Vault as VaultIcon } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const PRIMARY = [
  { label: "Cards", to: "/card-services" },
  { label: "Comics", to: "/comic-services" },
  { label: "Card Care Lab", to: "/card-care-lab" },
  { label: "Before & After", to: "/before-after" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Trust Center", to: "/trust-center" },
  { label: "About", to: "/about" },
];

const MOBILE_NAV = [
  { label: "Home", to: "/" },
  { label: "Cards", to: "/card-services" },
  { label: "Comics", to: "/comic-services" },
  { label: "Card Care Lab", to: "/card-care-lab" },
  { label: "Before & After", to: "/before-after" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Trust Center", to: "/trust-center" },
  { label: "After Hours", to: "/after-hours" },
  { label: "About The Lab", to: "/about" },
  { label: "The Library", to: "/library" },
  { label: "Our Process", to: "/our-process" },
  { label: "Shipping & Insurance", to: "/shipping-insurance" },
  { label: "Confidentiality", to: "/confidentiality" },
  { label: "Results", to: "/results" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const { user, logout } = useAuth();
  const authed = !!user;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = authed ? [{ label: "Vault", to: "/vault" }, ...PRIMARY] : [];

  return (
    <>
      <div className="hidden sm:block w-full bg-black border-b border-[color:var(--line)]">
        <div className="mx-auto max-w-[1400px] px-5 h-8 flex items-center justify-end">
          <span className="font-tech text-[10px] tracking-[0.42em] text-[color:var(--chrome-3)]">
            COLLECT MORE POSSIBILITIES
          </span>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-black/85 backdrop-blur-xl border-b border-[color:var(--line)]"
            : "bg-black border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 h-[72px] flex items-center justify-between gap-6">
          <div className="flex items-center gap-8">
            <div className="hidden md:block">
              <Logo />
            </div>
            <div className="md:hidden">
              <Logo markOnly />
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-tech text-[13px] tracking-wide hover-underline transition-colors ${
                  loc.pathname === n.to
                    ? "text-white"
                    : "text-[color:var(--chrome-2)] hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            {authed ? (
              <>
                <Link
                  to="/submit"
                  data-testid="nav-submit-cta"
                  className="hidden lg:inline-flex btn-holo rounded-lg px-4 py-2.5 text-[11px] mr-1"
                >
                  SUBMIT A COLLECTIBLE
                </Link>
                <Link
                  to="/vault"
                  data-testid="account-btn"
                  className="p-2.5 rounded-lg text-[color:var(--chrome-2)] hover:text-white hover:bg-white/5 transition-colors"
                  aria-label="Vault"
                >
                  <VaultIcon size={19} />
                </Link>
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="hidden sm:inline-flex p-2.5 rounded-lg text-[color:var(--chrome-2)] hover:text-white hover:bg-white/5 transition-colors"
                  aria-label="Sign out"
                >
                  <LogOut size={18} />
                </button>
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <button
                      data-testid="mobile-menu-btn"
                      className="lg:hidden p-2.5 rounded-lg text-white hover:bg-white/5 ml-1"
                      aria-label="Menu"
                    >
                      <Menu size={22} />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="bg-[color:var(--ink)] border-l border-[color:var(--line)] w-[300px] p-0">
                    <div className="p-6 border-b border-[color:var(--line)] flex items-center justify-between">
                      <Logo />
                      <button onClick={() => setMobileOpen(false)} className="text-white p-1">
                        <X size={20} />
                      </button>
                    </div>
                    <nav className="flex flex-col p-4 overflow-y-auto max-h-[calc(100vh-160px)]">
                      <Link to="/vault" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-vault"
                        className="flex items-center justify-between py-4 px-3 rounded-lg text-[color:var(--chrome-1)] hover:bg-white/5 font-tech tracking-wide border-b border-white/5">
                        The Vault <ArrowRight size={16} className="text-[color:var(--chrome-3)]" />
                      </Link>
                      {MOBILE_NAV.filter((n) => n.to !== "/").map((n) => (
                        <Link
                          key={n.to}
                          to={n.to}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="flex items-center justify-between py-4 px-3 rounded-lg text-[color:var(--chrome-1)] hover:bg-white/5 font-tech tracking-wide border-b border-white/5"
                        >
                          {n.label}
                          <ArrowRight size={16} className="text-[color:var(--chrome-3)]" />
                        </Link>
                      ))}
                      <button onClick={() => { setMobileOpen(false); logout(); }} data-testid="mobile-logout"
                        className="btn-ghost mt-6 rounded-lg py-3.5 text-center text-sm inline-flex items-center justify-center gap-2">
                        <LogOut size={15} /> Sign Out
                      </button>
                    </nav>
                  </SheetContent>
                </Sheet>
              </>
            ) : (
              <>
                <Link to="/signin" data-testid="header-signin"
                  className="font-tech text-[13px] tracking-wide text-[color:var(--chrome-2)] hover:text-white px-2 sm:px-3 py-2.5">
                  Sign In
                </Link>
                <Link to="/join" data-testid="header-open-account"
                  className="btn-holo rounded-lg px-3 sm:px-4 py-2.5 text-[11px] whitespace-nowrap">
                  <span className="sm:hidden">JOIN FREE</span><span className="hidden sm:inline">OPEN FREE ACCOUNT</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
};
